import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { calculateCreditPrice } from '@/config/stripe';

export const useStripePayment = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Vérifier l'authentification
  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('Vous devez être connecté pour effectuer cette action');
    }
    return session;
  };

  // Fonction générique pour créer une session checkout
  const createCheckoutSession = async (
    organizationId: string,
    type: 'branding_pro' | 'seats' | 'credits',
    options: Record<string, unknown>
  ) => {
    // Debug: Creating checkout session
    
    const { data, error } = await supabase.functions.invoke('create-checkout-session', {
      body: {
        organization_id: organizationId,
        type,
        ...options,
        success_url: `${window.location.origin}/subscription?success=true`,
        cancel_url: `${window.location.origin}/subscription`
      }
    });

    if (error) {
      console.error('Edge Function error:', error);
      throw new Error(error.message || 'Erreur lors de la création de la session de paiement');
    }
    
    if (!data || !data.checkout_url) {
      console.error('Invalid response from Edge Function:', data);
      throw new Error('Réponse invalide du serveur');
    }
    
    return data;
  };

  // Activer/désactiver Branding Pro
  const toggleBrandingPro = async (organizationId: string, enable: boolean) => {
    setLoading(true);
    setError(null);
    
    try {
      await checkAuth();
      if (enable) {
        // Créer un abonnement Branding Pro
        const { checkout_url } = await createCheckoutSession(organizationId, 'branding_pro', {
          enable: true
        });
        
        // Rediriger vers Stripe Checkout
        window.location.href = checkout_url;
      } else {
        // Désactiver via le portail client (pour annuler l'abonnement)
        await openCustomerPortal(organizationId);
      }
      
    } catch (err: unknown) {
      setError((err as Error).message || 'Erreur lors de la modification du Branding Pro');
    } finally {
      setLoading(false);
    }
  };

  // Modifier le nombre de seats
  const updateSeats = async (organizationId: string, seatCount: number) => {
    setLoading(true);
    setError(null);
    
    try {
      await checkAuth();
      const { checkout_url } = await createCheckoutSession(organizationId, 'seats', {
        seat_count: seatCount
      });
      
      // Rediriger vers Stripe Checkout
      window.location.href = checkout_url;
      
    } catch (err: unknown) {
      setError((err as Error).message || 'Erreur lors de la modification des seats');
    } finally {
      setLoading(false);
    }
  };

  // Acheter des crédits avec quantité variable
  const purchaseCredits = async (organizationId: string, quantity: number) => {
    setLoading(true);
    setError(null);
    
    try {
      await checkAuth();
      // Valider la quantité
      if (quantity < 1 || quantity > 1000) {
        throw new Error('La quantité doit être entre 1 et 1000 crédits');
      }

      // Calculer le prix avec le système dégressif
      calculateCreditPrice(quantity);

      const { checkout_url } = await createCheckoutSession(organizationId, 'credits', {
        credit_quantity: quantity
      });
      
      // Rediriger vers Stripe Checkout
      window.location.href = checkout_url;
      
    } catch (err: unknown) {
      setError((err as Error).message || 'Erreur lors de l\'achat de crédits');
    } finally {
      setLoading(false);
    }
  };

  // Ouvrir le portail client Stripe
  const openCustomerPortal = async (organizationId: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error } = await supabase.functions.invoke('create-customer-portal', {
        body: {
          organization_id: organizationId,
          return_url: `${window.location.origin}/subscription`
        }
      });

      if (error) throw error;
      
      // Rediriger vers le portail client
      window.location.href = data.portal_url;
      
    } catch (err: unknown) {
      setError((err as Error).message || 'Erreur lors de l\'ouverture du portail client');
    } finally {
      setLoading(false);
    }
  };

  // Récupérer le statut de l'abonnement depuis l'API
  const getSubscriptionStatus = async (organizationId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('get-subscription-status', {
        body: { organization_id: organizationId }
      });

      if (error) throw error;
      return data;
      
    } catch (err: unknown) {
      console.error('Error fetching subscription status:', err);
      throw err;
    }
  };

  return {
    loading,
    error,
    toggleBrandingPro,
    updateSeats,
    purchaseCredits,
    openCustomerPortal,
    getSubscriptionStatus,
    // Fonctions legacy pour compatibilité (à supprimer plus tard)
    createSubscription: () => {
      console.warn('createSubscription is deprecated, use toggleBrandingPro or updateSeats');
      return Promise.resolve();
    },
    updatePaymentMethod: (orgId: string) => openCustomerPortal(orgId),
    cancelSubscription: (orgId: string) => openCustomerPortal(orgId)
  };
};